// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PCurveGraph from "../PerformanceCurve/PCurveGraph";
import { editAsset, fetchAssetsByGroupId } from "../../store/assetsSlice";
import { fetchPCurves, fetchSolarEnergyModels } from "../../store/pCurveSlice";
import { closeModalWithResult } from "../../store/modalSlice";
import { ReactComponent as PowerIcon } from "../../assets/icons/lightning.svg";
import { ReactComponent as CloudRaining } from "../../assets/icons/cloud-raining-05.svg";
import { ReactComponent as WindIcon } from "../../assets/icons/wind-02.svg";
import { ReactComponent as SunIcon } from "../../assets/icons/sun.svg";
import ModalBase from "./ModalBase";

import Select from "../ReusableComponents/Select";
interface AssetPCurveEditProps {
  asset: any;
  onClose: (result?: any) => void;
}

export const foreCastSchema = {
  precip: {
    name: "Precipitation",
    short: "precip",
    Icon: CloudRaining,
  },
  wind: {
    name: "Wind",
    short: "wind",
    Icon: WindIcon,
  },
  solar: {
    name: "Solar",
    short: "solar",
    Icon: SunIcon,
  },
};

const AssetPCurveEdit: React.FC<AssetPCurveEditProps> = ({ asset, onClose }) => {
  const dispatch = useDispatch();
  const [activeForecast, setActiveForecast] = useState("wind");
  const { pCurve } = useSelector(({ assets, pCurve }) => ({
    assets,
    pCurve,
  }));
  const [selectedWind, setSelectedWind] = useState(asset.windCurveId);
  const [selectedSolar, setSelectedSolar] = useState(asset.solarCurveId);

  useEffect(() => {
    dispatch(fetchPCurves());
    dispatch(fetchSolarEnergyModels());
  }, [dispatch]);

  const windPCurveData = pCurve?.data?.[selectedWind];
  const solarEnergyModelData = pCurve?.solarData?.[selectedSolar];

  const handleClose = () => {
    onClose();
  };

  const handleUpdateCurve = async () => {
    try {
      // Include assetGroupId in the edit payload to ensure dataGroups is correctly updated
      await dispatch(
        editAsset({
         id: asset.id,
         organizationId: asset.organizationId,
          windCurveId: selectedWind,
          solarCurveId: selectedSolar
        })
      ).unwrap();
      
      // After updating the wind curve, fetch the assets again to ensure the dataGroups state is updated
    } catch (error) {
      // onClose({ success: false, error });
    }

    try {
      if (asset.assetGroupId) {
        await dispatch(fetchAssetsByGroupId(asset.assetGroupId));
      }
      
      onClose({ 
        success: true, 
        windCurveId: selectedWind, 
        solarCurveId: selectedSolar 
      });
    } catch (error) {
      console.log("other error", error)
    }
  };

  // Both forecast types share the same select + preview shape, so drive them from one entry
  const MODEL_PICKERS = {
    wind: {
      selectName: "windCurve",
      value: selectedWind,
      setValue: setSelectedWind,
      options: pCurve?.data,
      currentId: asset.windCurveId,
      emptyValue: "",
    },
    solar: {
      selectName: "solarCurve",
      value: selectedSolar,
      setValue: setSelectedSolar,
      options: pCurve?.solarData,
      currentId: asset.solarCurveId,
      emptyValue: "",
    },
  };

  const picker = MODEL_PICKERS[activeForecast];
  const selectedModel = activeForecast === "wind" ? windPCurveData : solarEnergyModelData;

  return (
    <ModalBase
      title="Assign Energy Models"
      icon={<PowerIcon className="h-6 w-6 [&_path]:stroke-current text-eai-hero-accent" />}
      onClose={handleClose}
      size="md"
      footer={
        <>
          <button className="eai-btn-outline" type="button" onClick={handleClose}>
            Cancel
          </button>
          <button className="eai-btn-cta" type="button" onClick={handleUpdateCurve}>
            Accept
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-5 md:flex-row">
        <div className="flex-1 min-w-0">
          <div className="eai-meta-label mb-2">Forecast type</div>
          <div className="mb-5 flex flex-wrap items-center gap-2">
            {Object.keys(foreCastSchema).map((key) => {
              const { Icon, short, name } = foreCastSchema[key];
              const isActive = activeForecast === short;
              return (
                <button
                  key={short}
                  type="button"
                  onClick={() => setActiveForecast(short)}
                  className={`eai-chip gap-1.5 ${isActive ? "eai-chip-active" : ""}`}
                >
                  <Icon className="h-4 w-4 [&_path]:stroke-current" />
                  {name}
                </button>
              );
            })}
          </div>

          {picker ? (
            <>
              <label htmlFor={picker.selectName} className="eai-meta-label mb-2 block">
                Energy model
              </label>
              <Select
                id={picker.selectName}
                name={picker.selectName}
                value={picker.value ?? picker.emptyValue}
                onChange={(e) => picker.setValue(e.target.value)}
                containerClassName="mb-3"
              >
                <option value={picker.emptyValue}>No Energy Model</option>
                {Object.values(picker.options || {}).map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} {picker.currentId === model.id && "- Current"}
                  </option>
                ))}
              </Select>

              {selectedModel?.description && (
                <p className="eai-description-sm">{selectedModel.description}</p>
              )}
            </>
          ) : (
            <p className="eai-description-sm">
              No energy models are assigned to {foreCastSchema[activeForecast]?.name.toLowerCase()} forecasts.
            </p>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {activeForecast === "wind" && windPCurveData && (
            <PCurveGraph
              data={windPCurveData.curveData}
              xAxisLabel={windPCurveData.xUnit}
              yAxisLabel={windPCurveData.yUnit}
            />
          )}
          {activeForecast === "solar" && solarEnergyModelData && (
            <div className="eai-card p-4">
              {Object.keys(solarEnergyModelData).map((key) => (
                <div key={key} className="mb-2 flex gap-2 last:mb-0">
                  <span className="eai-meta-label shrink-0 pt-0.5">{key}</span>
                  <span className="eai-description-sm min-w-0 break-words text-eai-ink">
                    {String(solarEnergyModelData[key])}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
};

export default AssetPCurveEdit;
